import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  FiCheckCircle,
  FiClock,
  FiLock,
  FiMoreVertical,
  FiPlay,
  FiTrash,
} from 'react-icons/fi';
import {
  formatAppointmentClock,
  canStartExamNow,
  type AppointmentRead,
} from '../../../helpers/api/appointment/appointment';
import {
  formatStaffName,
  getAppointmentStatusVisual,
  getPriorityVisual,
} from './schedulingStatus';

type Props = {
  app: AppointmentRead;
  variant?: 'day' | 'week' | 'worklist';
  highlight?: boolean;
  showAssignee?: boolean;
  canWrite?: boolean;
  assigneeFallbackById?: Map<string, string>;
  onStartExam?: (app: AppointmentRead) => void;
  onCancel?: (id: string) => void;
  onCheckIn?: (id: string) => void;
  onComplete?: (id: string) => void;
  onSelect?: (app: AppointmentRead) => void;
  className?: string;
};

export default function AppointmentCard({
  app,
  variant = 'day',
  highlight = false,
  showAssignee = false,
  canWrite = false,
  assigneeFallbackById,
  onStartExam,
  onCancel,
  onSelect,
  className,
}: Props) {
  const { t } = useTranslation('Scheduling');
  const status = getAppointmentStatusVisual(app, t);
  const priority = getPriorityVisual(app.priority, t);
  const typeKey = String(app.appointment_type || '').toLowerCase();
  const typeLabel = typeKey
    ? t(`modal.scanTypes.${typeKey}`, { defaultValue: app.appointment_type })
    : '—';
  const assignee =
    formatStaffName(
      app.assignee_first_name,
      app.assignee_last_name,
      app.assignee_email,
      assigneeFallbackById,
      app.doctor_id
    ) || t('staff');
  const bookedBy =
    app.created_by_id && String(app.created_by_id) !== String(app.doctor_id)
      ? formatStaffName(
          app.booked_by_first_name,
          app.booked_by_last_name,
          app.booked_by_email,
          assigneeFallbackById,
          app.created_by_id
        )
      : '';
  const st = String(app.status).toLowerCase().replace(/^.*\./, '');
  const canStart =
    (st === 'scheduled' || st === 'confirmed' || st === 'in_progress') &&
    canStartExamNow(app);
  const isCompact = variant === 'week';

  return (
    <article
      id={`appointment-${app.id}`}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={() => onSelect?.(app)}
      onKeyDown={(event) => {
        if (onSelect && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onSelect(app);
        }
      }}
      className={cn(
        'group relative w-full border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800',
        'border-l-4 shadow-sm transition-all',
        onSelect &&
          'cursor-pointer hover:border-blue-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        status.accentClass,
        isCompact ? 'rounded-lg p-2.5' : 'rounded-xl px-4 py-3.5',
        status.isLocked && 'opacity-75',
        highlight && 'ring-2 ring-blue-500 ring-offset-2',
        className
      )}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-800 dark:text-gray-100">
              <FiClock size={13} className="text-blue-500 shrink-0" />
              {formatAppointmentClock(app.start_time)}
            </span>
            {priority && (
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                  priority.className
                )}
              >
                {priority.label}
              </span>
            )}
            <span
              className={cn(
                'rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                status.badgeClass
              )}
            >
              {status.label}
            </span>
            {app.source === 'mwl_import' && (
              <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                MWL
              </span>
            )}
          </div>

          <p
            className={cn(
              'font-semibold text-slate-900 dark:text-white truncate',
              isCompact ? 'text-xs' : 'text-base'
            )}
          >
            {app.patient_name} {app.patient_last_name}
          </p>

          <div
            className={cn(
              'mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-slate-500 dark:text-gray-400',
              isCompact ? 'text-[10px]' : 'text-[13px]'
            )}
          >
            <span>{typeLabel}</span>
            <span>·</span>
            <span>{t('durationMin', { minutes: app.duration_minutes || 30 })}</span>
            {app.patient_mrn && (
              <>
                <span>·</span>
                <span>MRN {app.patient_mrn}</span>
              </>
            )}
            {app.accession_number && st !== 'draft' && (
              <>
                <span>·</span>
                <span className="font-mono text-blue-600 dark:text-blue-400">
                  {app.accession_number}
                </span>
              </>
            )}
          </div>

          {(showAssignee || bookedBy) && (
            <p className="mt-1 text-[11px] text-slate-400 truncate">
              {showAssignee && t('assigned', { name: assignee })}
              {showAssignee && bookedBy ? ' · ' : ''}
              {bookedBy ? t('bookedBy', { name: bookedBy }) : ''}
            </p>
          )}
        </div>

        <div className="shrink-0 flex items-center gap-1">
          {!status.isLocked && canStart && onStartExam && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onStartExam(app);
              }}
              className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-blue-900/30"
              aria-label={t('startExam')}
              title={t('startExam')}
            >
              <FiPlay size={14} />
            </button>
          )}

          {status.isLocked ? (
            <span className="p-1.5 text-slate-300" title={t('locked')}>
              <FiLock size={14} />
            </span>
          ) : (
            <div className="relative dui-dropdown dui-dropdown-end">
              <button
                type="button"
                tabIndex={0}
                onClick={(event) => event.stopPropagation()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
                aria-label={t('actions.more', { defaultValue: 'More actions' })}
                aria-haspopup="menu"
              >
                <FiMoreVertical size={16} />
              </button>
              <ul
                tabIndex={0}
                role="menu"
                className="dui-dropdown-content dui-menu z-50 mt-1 w-44 rounded-xl border border-base-300 bg-base-100 p-1.5 text-base-content shadow-xl"
              >
                {canStart && onStartExam && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-blue-600"
                      onClick={(event) => {
                        event.stopPropagation();
                        onStartExam(app);
                      }}
                    >
                      <FiPlay size={12} /> {t('startExam')}
                    </button>
                  </li>
                )}
                {canWrite && onCancel && !status.isLocked && (
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-red-500"
                      onClick={(event) => {
                        event.stopPropagation();
                        onCancel(String(app.id));
                      }}
                    >
                      <FiTrash size={12} /> {t('cancel')}
                    </button>
                  </li>
                )}
                {status.statusKey === 'completed' && (
                  <li role="none">
                    <span className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400">
                      <FiCheckCircle size={12} /> {status.label}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
